import { atom, useSetRecoilState } from "recoil";
import { Module } from "../models/common/modules";
import { Country, DocumentMessageSharingType } from "../models/generated/graphql";
import { Role } from "../models/identity/role";
import IUserDataProps, { ExposedResource } from "../models/identity/user-data";

export const personalSettingsTabState = 0;

type ActiveTab = {
	key: "activeUserTab";
	default: number;
};

const initialActiveUserTabState: ActiveTab = {
	key: "activeUserTab",
	default: personalSettingsTabState,
};

export const organizationSettingsTabState = 0;

type OrganizationActiveTab = {
	key: "activeOrganizationTab";
	default: number;
};

const initialActiveOrganizationTabState: OrganizationActiveTab = {
	key: "activeOrganizationTab",
	default: personalSettingsTabState,
};

export const activeUserTabState = atom(initialActiveUserTabState);
export const activeOrganizationTabState = atom(initialActiveOrganizationTabState);

export type UserPersonalInfo = {
	id: string;
	firstName: string;
	lastName: string;
	currentLoginDate?: string;
	previousLoginDate?: string;
	email: string;
	isGuest: boolean;
	exposedResource: ExposedResource | null;
	department: string;
	position: string;
	organization: { name: string; externalId: string };
	preferredLanguage: string;
	currency?: string;
	dosageMeasureUnit?: string;
	modules: Module[];
	role: Role;
	userImg: string | null;
	sourceUserImage: string | null;
	userSignature: string | null;
	sourceUserSignature: string | null;
	isSignatureCreationDraw: boolean | null;
	notificationCount: number;
	isSubscriptionActive: boolean;
	questsCount: number;
	agreementsTableColumns: Array<string>;
	transactionsInRefundInvoiceDocumentTableColumns: Array<string>;
	transactionsInCreditNoteDocumentTableColumns: Array<string>;
	transactionsInFreeOfChargeDocumentTableColumns: Array<string>;
	simulationsTableColumns: Array<string>;
	transactionsTableColumns: Array<string>;
	documentsTableColumns: Array<string>;
	recordsTableColumns: Array<string>;
	formsTableColumns: Array<string>;
	simulatorWasteReportingEnabled?: boolean;
	isSimulationOwner: boolean;
	domains: Array<{ id: string; level: number }>;
};

type User = {
	id: string;
	firstName: string;
	lastName: string;
	currentLoginDate?: string;
	previousLoginDate?: string;
	email: string;
	isGuest: boolean;
	exposedResource: ExposedResource | null;
	department: string;
	position: string;
	organization: { name: string; externalId: string; country?: Country };
	userImg: string | null;
	sourceUserImage: string | null;
	userSignature: string | null;
	sourceUserSignature: string | null;
	isSignatureCreationDraw: boolean | null;
	preferredLanguage: string;
	currency?: string;
	dosageMeasureUnit?: string;
	modules: Module[];
	role: Role;
	isSubscriptionActive: boolean;
	notificationCount: number;
	questsCount: number;
	agreementsTableColumns: Array<string>;
	transactionsInRefundInvoiceDocumentTableColumns: Array<string>;
	transactionsInCreditNoteDocumentTableColumns: Array<string>;
	transactionsInFreeOfChargeDocumentTableColumns: Array<string>;
	simulationsTableColumns: Array<string>;
	transactionsTableColumns: Array<string>;
	documentsTableColumns: Array<string>;
	recordsTableColumns: Array<string>;
	formsTableColumns: Array<string>;
	isSimulationOwner: boolean;
	simulatorWasteReportingEnabled?: boolean;
	domains: Array<{ id: string; level: number }>;
	defaultMessageSharingType: DocumentMessageSharingType;
};

type UserPersonalInfoState = {
	key: "userPersonalInfo";
	default: User;
};

const initialUserPersonalInfo: UserPersonalInfoState = {
	key: "userPersonalInfo",
	default: {
		id: "",
		firstName: "",
		lastName: "",
		currentLoginDate: "",
		previousLoginDate: "",
		email: "",
		isGuest: false,
		exposedResource: null,
		department: "",
		position: "",
		organization: { name: "", externalId: "" },
		userImg: null,
		sourceUserImage: null,
		userSignature: null,
		sourceUserSignature: null,
		isSignatureCreationDraw: null,
		preferredLanguage: "en-US",
		currency: "USD",
		dosageMeasureUnit: "mg",
		modules: [],
		role: Role.Unknown,
		isSubscriptionActive: false,
		notificationCount: 0,
		questsCount: 0,
		agreementsTableColumns: [],
		transactionsInRefundInvoiceDocumentTableColumns: [],
		transactionsInCreditNoteDocumentTableColumns: [],
		transactionsInFreeOfChargeDocumentTableColumns: [],
		simulationsTableColumns: [],
		transactionsTableColumns: [],
		documentsTableColumns: [],
		recordsTableColumns: [],
		formsTableColumns: [],
		isSimulationOwner: false,
		simulatorWasteReportingEnabled: true,
		domains: [],
		defaultMessageSharingType: DocumentMessageSharingType.External,
	},
};

export const userPersonalInfoState = atom(initialUserPersonalInfo);

export const useSetUserFromUserDataProps = () => {
	const setUserPersonalInfo = useSetRecoilState(userPersonalInfoState);

	return (user: IUserDataProps) => {
		const {
			id = "",
			firstName = "",
			lastName = "",
			currentLoginDate = "",
			previousLoginDate = "",
			email = "",
			isGuest = false,
			exposedResource = null,
			department = "",
			position = "",
			preferredLanguage,
			currency,
			dosageMeasureUnit,
			organization: { name, externalId, country } = { name: "", externalId: "" },
			modules = [],
			userImg = null,
			sourceUserImage = null,
			userSignature = null,
			sourceUserSignature = null,
			isSignatureCreationDraw = null,
			role = Role.Unknown,
			isSubscriptionActive = false,
			notificationCount = 0,
			questsCount = 0,
			agreementsTableColumns = [],
			transactionsInRefundInvoiceDocumentTableColumns = [],
			transactionsInCreditNoteDocumentTableColumns = [],
			transactionsInFreeOfChargeDocumentTableColumns = [],
			simulationsTableColumns = [],
			transactionsTableColumns = [],
			documentsTableColumns = [],
			recordsTableColumns = [],
			formsTableColumns = [],
			isSimulationOwner = false,
			simulatorWasteReportingEnabled,
			domains,
			defaultMessageSharingType = DocumentMessageSharingType.External,
		} = user;

		setUserPersonalInfo({
			id,
			firstName,
			lastName,
			currentLoginDate,
			previousLoginDate,
			email,
			isGuest,
			exposedResource,
			department,
			position,
			preferredLanguage,
			currency,
			dosageMeasureUnit,
			organization: { name, externalId, country },
			modules,
			userImg,
			sourceUserImage,
			userSignature,
			sourceUserSignature,
			isSignatureCreationDraw,
			role,
			isSubscriptionActive,
			notificationCount,
			questsCount,
			agreementsTableColumns,
			simulationsTableColumns,
			transactionsTableColumns,
			transactionsInRefundInvoiceDocumentTableColumns,
			transactionsInCreditNoteDocumentTableColumns,
			transactionsInFreeOfChargeDocumentTableColumns,
			isSimulationOwner,
			simulatorWasteReportingEnabled,
			domains,
			defaultMessageSharingType,
			documentsTableColumns,
			recordsTableColumns,
			formsTableColumns,
		});
	};
};
